import { useMemo, useState } from 'react';
import {
  UseMutationResult,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { APIError, DiseaseSearchParams, ICDCodesSearchParams } from '@data';
import {
  diseasesKeys,
  icdCodeNamesTranslationsKeys,
  icdCodeTranslationsKeys,
  icdCodesKeys,
} from '@data/constants';
import {
  DiseaseNameCreate,
  DiseaseNameUpdateQuery,
  DiseaseNameView,
  DiseasesApiGetDiseasesRequest,
  DiseasesApiGetDiseasesV2Request,
  DiseasesApiGetDiseasesTrialsCountRequest,
  DiseasesApiGetIcdDiseasesRequest,
  DiseaseTranslationsBody,
  DiseaseTrialsCountView,
  DiseaseView,
  ListDiseaseNameView,
  ListDiseaseTranslationView,
  ListDiseaseView,
  ListDiseaseV2View,
  ListIcdDiseaseView,
} from '@data/sdk/match';
import { MatchingApi } from '@data/utils/axios-instance';
import { Language } from '@data/utils/enums';
import i18n from '../../../app/i18n';
import { useDebounce } from '../../../app/utils/hooks/useDebounce.hook';

export type DiseaseOption = {
  categoryId?: string;
  dataTestId: string;
  icdCode: string;
  icdId: string;
  id: string;
  label: string;
  trialCount: number;
  value: string;
};

export const useDiseasesInfiniteList = () => {
  const [query, setQuery] = useState<DiseaseSearchParams>({
    limit: 15,
    offset: 0,
    searchString: undefined,
    language: i18n.language as Language,
  });

  const debouncedSearch = useDebounce(query.searchString);

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching } = useInfiniteQuery<
    ListDiseaseView,
    APIError
  >({
    queryKey: diseasesKeys.infiniteList({
      ...query,
      searchString: debouncedSearch,
      language: i18n.language as Language,
    }),
    initialPageParam: {
      ...query,
      searchString: debouncedSearch,
      language: i18n.language as Language,
    },
    queryFn: async ({ pageParam }) => {
      const response = await MatchingApi.diseases.getDiseases(
        pageParam as DiseasesApiGetDiseasesRequest,
      );
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextOffset = allPages.reduce((prev, next) => prev + next.items.length, 0);

      return nextOffset < lastPage.count
        ? {
            limit: query.limit,
            offset: nextOffset,
            searchString: debouncedSearch || undefined,
            language: i18n.language as Language,
          }
        : undefined;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    gcTime: 1000 * 60 * 60,
    staleTime: 1000 * 60 * 60,
  });

  const diseases = data?.pages.flatMap((page) => page.items) || [];

  const diseaseOptions: DiseaseOption[] = useMemo(
    () =>
      diseases
        .filter(
          (disease, index, self) =>
            index ===
            self.findIndex((d) => d.name === disease.name && d.icdCode === disease.icdCode),
        )
        .map((disease) => ({
          label: disease.name,
          value: disease.name,
          dataTestId: disease.name,
          id: disease.id,
          icdId: disease.icdCode,
          trialCount: disease.trialCount,
          categoryId: disease.categoryId,
          icdCode: disease.icdCode,
        })),
    [diseases],
  );

  const handleSearchChange = (v?: string) => {
    setQuery({ ...query, searchString: v || undefined, offset: 0 });
  };

  return {
    diseases,
    diseaseOptions,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoading || isFetching,
    handleSearchChange,
    searchString: query.searchString,
  };
};

export const useDiseasesPaginatedList = (
  searchParams: DiseasesApiGetDiseasesRequest,
  enabled = true,
) =>
  useQuery<ListDiseaseView, Error>({
    queryKey: diseasesKeys.list(searchParams),
    queryFn: async () => {
      const response = await MatchingApi.diseases.getDiseases(searchParams);
      return response.data;
    },
    enabled,
  });

export const useICDCodesList = (showFullName = false) => {
  const [query, setQuery] = useState<ICDCodesSearchParams>({
    limit: 15,
    offset: 0,
    searchString: undefined,
  });

  const debouncedSearch = useDebounce(query.searchString);

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching } = useInfiniteQuery<
    ListIcdDiseaseView,
    APIError
  >({
    queryKey: icdCodesKeys.list({ ...query, searchString: debouncedSearch }),
    initialPageParam: { ...query, searchString: debouncedSearch },
    queryFn: async ({ pageParam }) => {
      const response = await MatchingApi.diseases.getIcdDiseases(
        pageParam as DiseasesApiGetIcdDiseasesRequest,
      );
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextOffset = allPages.reduce((prev, next) => prev + next.items.length, 0);

      return nextOffset < lastPage.count
        ? {
            limit: query.limit,
            offset: nextOffset,
            searchString: debouncedSearch || undefined,
          }
        : undefined;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    gcTime: 1000 * 60 * 60,
    staleTime: 1000 * 60 * 60,
  });

  const icdCodes = data?.pages.flatMap((page) => page.items) || [];

  const icdCodesOptions = useMemo(
    () =>
      icdCodes.map((code) => ({
        label: showFullName ? `${code.icdCode} - ${code.name}` : code.icdCode,
        value: code.icdCode,
        id: code.id,
      })),
    [icdCodes],
  );

  const handleSearchChange = (v?: string) => {
    setQuery({ ...query, searchString: v ?? undefined, offset: 0 });
  };

  return {
    icdCodes,
    icdCodesOptions,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoading || isFetching,
    handleSearchChange,
    searchString: query.searchString,
  };
};

export const useICDCodesPaginatedList = (searchParams: ICDCodesSearchParams) =>
  useQuery<ListIcdDiseaseView, Error>({
    queryKey: icdCodesKeys.list(searchParams),
    queryFn: async () => {
      const response = await MatchingApi.diseases.getIcdDiseases(searchParams);
      return response.data;
    },
    staleTime: 1000 * 60 * 60,
  });

export const useICDCodesTranslationsOnMutation = () =>
  useMutation<ListIcdDiseaseView, Error, DiseasesApiGetIcdDiseasesRequest>({
    mutationFn: async (params) => {
      const response = await MatchingApi.diseases.getIcdDiseases(params);
      return response.data;
    },
  });

export const useDiseasesV2BatchOnMutation = () =>
  useMutation<ListDiseaseV2View[], Error, DiseasesApiGetDiseasesV2Request[]>({
    mutationFn: async (paramsList) => {
      const responses = await Promise.all(
        paramsList.map((params) => MatchingApi.diseases.getDiseasesV2(params)),
      );
      return responses.map((response) => response.data);
    },
  });

export const useICDCodeTranslations = (id: string) =>
  useQuery<ListDiseaseTranslationView, Error>({
    queryKey: icdCodeTranslationsKeys.list(id),
    queryFn: async () => {
      const response = await MatchingApi.diseases.getIcdDiseaseTranslations({ id });
      return response.data;
    },
  });

export const useICDCodeNamesTranslations = (id: string, searchParams: DiseaseSearchParams) =>
  useQuery<ListDiseaseNameView, Error>({
    queryKey: icdCodeNamesTranslationsKeys.detail(id, searchParams),
    queryFn: async () => {
      const response = await MatchingApi.diseases.getIcdDiseaseNames({ id, ...searchParams });
      return response.data;
    },
    staleTime: 1000 * 60 * 60,
  });

export const useEditICDCodeTranslation = (): UseMutationResult<
  ListDiseaseTranslationView,
  any,
  { id: string; values: DiseaseTranslationsBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }) => {
      const response = await MatchingApi.diseases.upsertIcdDiseaseTranslations({
        id,
        diseaseTranslationsBody: values,
      });
      return response.data;
    },
    onSuccess: (_, { id }) =>
      queryClient.invalidateQueries({ queryKey: icdCodeTranslationsKeys.list(id) }),
  });
};

export const useEditICDCodeNameTranslation = (): UseMutationResult<
  DiseaseNameView,
  any,
  { id: string; values: DiseaseNameUpdateQuery },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }) => {
      const response = await MatchingApi.diseases.updateDiseaseName({
        id,
        diseaseNameUpdateQuery: values,
      });
      return response.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: icdCodeNamesTranslationsKeys.all() }),
  });
};

export const useCreateICDCodeName = (): UseMutationResult<
  DiseaseView,
  any,
  { id: string; values: DiseaseNameCreate },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }) => {
      const response = await MatchingApi.diseases.createIcdDiseaseName({
        id,
        diseaseNameCreate: values,
      });
      return response.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: icdCodeNamesTranslationsKeys.all() }),
  });
};

export const useDeleteICDCodeName = (): UseMutationResult<void, any, { id: string }, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }) => {
      const response = await MatchingApi.diseases.deleteDiseaseName({ id });
      return response.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: icdCodeNamesTranslationsKeys.all() }),
  });
};

export const useDiseasesTrialsCount = (
  params: DiseasesApiGetDiseasesTrialsCountRequest,
  enabled = true,
) =>
  useQuery<DiseaseTrialsCountView, Error>({
    queryKey: diseasesKeys.trialsCount(params),
    queryFn: async () => {
      const response = await MatchingApi.diseases.getDiseasesTrialsCount(params);
      return response.data;
    },
    enabled,
  });

export const useDiseasesTrialsCountOnMutate = () =>
  useMutation<DiseaseTrialsCountView, Error, DiseasesApiGetDiseasesTrialsCountRequest>({
    mutationFn: async ({ countryFilter, icdCodes }) => {
      const response = await MatchingApi.diseases.getDiseasesTrialsCount({
        countryFilter,
        icdCodes,
      });
      return response.data;
    },
  });
